import { FormBuilderState } from "components/containers/inspectionSetting/containers/templateBuilder/context/FormBuilderContext/types";
import { pressureOptions } from "components/containers/inspectionSetting/containers/templateBuilder/fields/device-pressure/previewer";
import BlackboardContentText from "components/containers/inspectionSetting/containers/templateBuilder/fields/imageHasBlackboard/previewer/BlackboardContentText";
import {
  DataType,
  types,
} from "components/containers/inspectionSetting/containers/templateBuilder/setup/define";
import CheckboxIcon from "components/icon/CheckboxIcon";
import ConnectDeviceIcon from "components/icon/ConnectDeviceIcon";
import PinOnMapIcon from "components/icon/PinOnMapIcon";
import Sort123Icon from "components/icon/Sort123Icon";
import TyphographyIcon from "components/icon/TyphographyIcon";
import { getContentNodeTypeNormalText } from "components/TipTapEditorDoc/utils";
import {
  GetBlackboardContent,
  GetContentPreview,
} from "interfaces/models/inspectionDocument";
import { InspectionItemResult } from "interfaces/models/inspectionItemResult";
import {
  LinkedDataAssetKey,
  LinkedDataHeadKey,
} from "interfaces/models/pinComponent";
import { generateSourceBimId } from "models/inspection-instances";
import { transformCheckboxExtraKey } from "models/pinFrame";
import { SOURCE } from "../../context/pinLayoutFrameReducer";
import { Category } from "../../hooks/useFetchCategories";

export interface FieldRegister {
  id: string;
  name: string;
  icon: any;
  [key: string]: any;
  getContentPreview?: GetContentPreview;
  getBlackboardContent?: GetBlackboardContent;
}

export type Ctx = {
  formBuilderState?: FormBuilderState;
  categories?: Category[];
};

function getNoForContentPreview(_: any, params: any) {
  const { pinDetail, component } = params;
  const text = pinDetail?.no || "";
  const content = getContentNodeTypeNormalText({
    text,
    defaultText: text,
    style: component?.style,
    options: {
      isInt: true,
    },
  });
  const updatedAt = pinDetail?.updatedAt;

  return { content, updatedAt };
}

export const assetsPartials: FieldRegister[] = [
  {
    id: LinkedDataAssetKey.LIST_NUMBER,
    name: "リスト通し番号",
    closeable: false,
    icon: <Sort123Icon />,
    getContentPreview: getNoForContentPreview,
  },
  {
    id: LinkedDataAssetKey.FREEWORD_INPUT,
    name: "フリーテキスト欄",
    closeable: false,
    icon: <TyphographyIcon />,
  },
];

export const headPartials: FieldRegister[] = [
  {
    id: LinkedDataHeadKey.NO,
    name: "No.",
    icon: <PinOnMapIcon w="3.2rem" h="4.6rem" />,
    getContentPreview: getNoForContentPreview,
    getBlackboardContent: (params) => {
      const { pinDetail, style, zoomRatio } = params;
      const index = pinDetail?.index ?? 0;
      const value = pinDetail?.no ?? index + 1;

      return (
        <BlackboardContentText
          value={`${value}`}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },
  {
    id: LinkedDataHeadKey.NAME,
    name: "ピン名称",
    icon: <TyphographyIcon />,
    getContentPreview: (_, params) => {
      const { pinDetail, component } = params;
      const content = getContentNodeTypeNormalText({
        text: pinDetail?.label || "",
        style: component?.style,
      });
      const updatedAt = pinDetail?.updatedAt;

      return { content, updatedAt };
    },
    getBlackboardContent: (params) => {
      const { pinDetail, style, zoomRatio } = params;

      return (
        <BlackboardContentText
          value={pinDetail?.label || ""}
          style={style}
          zoomRatio={zoomRatio}
        />
      );
    },
  },
];

// Register all fields using to create pin frame
export const registers = [
  {
    source: SOURCE.HEAD,
    fields: headPartials,
  },
  {
    source: SOURCE.ASSET,
    fields: assetsPartials,
  },
  {
    source: SOURCE.INSPECTION_FORM,
    fields: (ctx: Ctx): FieldRegister[] => {
      const fields: any[] = [];

      if (ctx.formBuilderState?.itemResults) {
        ctx.formBuilderState?.itemResults?.map(
          (inspectionItemResult: InspectionItemResult) => {
            switch (inspectionItemResult.dataType) {
              case DataType.CHECKBOX:
                const additionInfoCheckbox: any = Object.values(
                  inspectionItemResult?.additionInfo?.checkbox?.mapValues || {}
                );

                return additionInfoCheckbox?.map(
                  ({ displayValue }: any, index: any) => {
                    const { normalizeCheckboxValue, normalizeCheckboxLabel } =
                      transformCheckboxExtraKey(index);

                    return fields.push(
                      {
                        id: inspectionItemResult.id,
                        name: displayValue,
                        icon: <CheckboxIcon w="1.5rem" h="1.5rem" />,
                        extraId: normalizeCheckboxValue,
                      },
                      {
                        id: inspectionItemResult.id,
                        name: displayValue,
                        icon: <TyphographyIcon w="1.5rem" h="1.5rem" />,
                        extraId: normalizeCheckboxLabel,
                      }
                    );
                  }
                );

              case DataType.DEVICE_PRESSURE:
                return pressureOptions.map(({ value, label }) => {
                  return fields.push({
                    id: inspectionItemResult.id,
                    name: label,
                    icon: <ConnectDeviceIcon w="1.5rem" h="1.5rem" />,
                    extraId: value,
                  });
                });
            }

            return fields.push({
              id: inspectionItemResult.id,
              name: inspectionItemResult.name,
              icon: types[inspectionItemResult.dataType]?.icon ?? (
                <TyphographyIcon />
              ),
            });
          }
        );
      }

      return fields;
    },
  },
  {
    source: SOURCE.BIM,
    fields: (ctx: Ctx): FieldRegister[] => {
      const fields: FieldRegister[] = [];
      ctx.categories?.forEach(({ name: category, properties }) => {
        properties.forEach(({ name, title, displayCategory }) => {
          fields.push({
            id: generateSourceBimId(category, name),
            extraId: category,
            name: `${displayCategory ? `【${displayCategory}】` : ""}${title}`,
            icon: <TyphographyIcon />,
          });
        });
      });

      return fields;
    },
  },
];
